import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface FloatingActionBarProps {
  /** Number of currently selected items. Bar is hidden when 0. */
  selectedCount: number;
  children?: ReactNode;
  itemLabelKey?: string;
}

/**
 * Reusable sticky action bar that appears when one or more rows are selected.
 * It renders a translated selection label and any action buttons passed as children.
 */
export function FloatingActionBar({
  selectedCount,
  children,
  itemLabelKey = 'common.floatingBar.itemsSelected',
}: FloatingActionBarProps) {
  const { t } = useTranslation();

  if (selectedCount === 0) return null;

  const hasActions = Boolean(children);

  return (
    <div className="border-border bg-surface shadow-md fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-xl border px-4 py-2.5">
      <span className="text-onsurface/70 text-sm font-medium whitespace-nowrap">
        {t(itemLabelKey, { count: selectedCount })}
      </span>

      {hasActions ? (
        <>
          <div className="bg-border h-4 w-px shrink-0" />
          {children}
        </>
      ) : null}
    </div>
  );
}
